/**
 * Content Hub Analytics API Service
 */

import { api, handleApiError } from '../client'

// ============================================
// Types
// ============================================

export interface ContentHubStatsDto {
  totalItems: number
  textElements: number
  dataPoints: number
  imageElements: number
  tableElements: number
  emailSnippets: number
  slideElements: number
  totalViews: number
  totalInserts: number
  totalDownloads: number
  totalSearches: number
  uniqueUsers: number
  viewsChangePercent: number
  insertsChangePercent: number
}

export interface TopContentItemDto {
  id: string
  name: string
  contentType: string
  folderPath?: string
  viewCount: number
  insertCount: number
  downloadCount: number
  copyCount: number
  totalUsage: number
  lastUsedAt?: string
  usedByApps: string[]
}

export interface UsageTrendPointDto {
  date: string
  views: number
  inserts: number
  downloads: number
  copies: number
  searches: number
}

export interface ContentTypeUsageDto {
  contentType: string
  itemCount: number
  usageCount: number
  percentage: number
}

export interface UserContentActivityDto {
  userId: string
  userName: string
  viewCount: number
  insertCount: number
  createCount: number
  totalActions: number
  lastActiveAt: string
}

export interface SearchQueryAnalyticsDto {
  query: string
  searchCount: number
  avgResultCount: number
  clickThroughRate: number
}

export interface ContentFreshnessDto {
  updatedLast7Days: number
  updatedLast30Days: number
  updatedLast90Days: number
  updatedOver90Days: number
  neverUsed: number
  staleItems: StaleContentItemDto[]
}

export interface StaleContentItemDto {
  id: string
  name: string
  contentType: string
  createdAt: string
  lastUpdatedAt?: string
  lastUsedAt?: string
  daysSinceLastUse: number
}

export interface ContentAnalyticsOverviewDto {
  stats: ContentHubStatsDto
  topItems: TopContentItemDto[]
  leastUsedItems: TopContentItemDto[]
  usageTrend: UsageTrendPointDto[]
  usageByType: ContentTypeUsageDto[]
  topUsers: UserContentActivityDto[]
  popularSearches: SearchQueryAnalyticsDto[]
  noResultSearches: SearchQueryAnalyticsDto[]
  freshness: ContentFreshnessDto
}

export interface AppUsageBreakdownDto {
  appContext: string
  usageCount: number
  percentage: number
}

export interface ContentItemAnalyticsDto {
  contentItemId: string
  contentItemName: string
  contentType: string
  totalViews: number
  totalInserts: number
  totalDownloads: number
  totalCopies: number
  lastUsedAt?: string
  usageTrend: UsageTrendPointDto[]
  appBreakdown: AppUsageBreakdownDto[]
  topUsers: UserContentActivityDto[]
}

export interface RecordContentEventRequest {
  contentItemId?: string
  contentItemName?: string
  contentType?: string
  eventType: string
  appContext?: string
  searchQuery?: string
  searchResultCount?: number
  metadata?: Record<string, unknown>
}

// ============================================
// Service
// ============================================

export const contentHubAnalyticsService = {
  /**
   * Get analytics overview
   */
  async getOverview(startDate?: Date, endDate?: Date): Promise<ContentAnalyticsOverviewDto> {
    try {
      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate.toISOString())
      if (endDate) params.set('endDate', endDate.toISOString())

      const response = await api.get<ContentAnalyticsOverviewDto>(
        `/v1/content-hub/analytics/overview?${params}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get content hub stats
   */
  async getStats(startDate?: Date, endDate?: Date): Promise<ContentHubStatsDto> {
    try {
      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate.toISOString())
      if (endDate) params.set('endDate', endDate.toISOString())

      const response = await api.get<ContentHubStatsDto>(`/v1/content-hub/analytics/stats?${params}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get top content items
   */
  async getTopItems(
    startDate?: Date,
    endDate?: Date,
    count: number = 10
  ): Promise<TopContentItemDto[]> {
    try {
      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate.toISOString())
      if (endDate) params.set('endDate', endDate.toISOString())
      params.set('count', count.toString())

      const response = await api.get<TopContentItemDto[]>(
        `/v1/content-hub/analytics/top-items?${params}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get usage trends
   */
  async getTrends(startDate?: Date, endDate?: Date): Promise<UsageTrendPointDto[]> {
    try {
      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate.toISOString())
      if (endDate) params.set('endDate', endDate.toISOString())

      const response = await api.get<UsageTrendPointDto[]>(
        `/v1/content-hub/analytics/trends?${params}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get search query analytics
   */
  async getSearchQueries(
    startDate?: Date,
    endDate?: Date,
    count: number = 20
  ): Promise<SearchQueryAnalyticsDto[]> {
    try {
      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate.toISOString())
      if (endDate) params.set('endDate', endDate.toISOString())
      params.set('count', count.toString())

      const response = await api.get<SearchQueryAnalyticsDto[]>(
        `/v1/content-hub/analytics/search-queries?${params}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get item-specific analytics
   */
  async getItemAnalytics(
    itemId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<ContentItemAnalyticsDto> {
    try {
      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate.toISOString())
      if (endDate) params.set('endDate', endDate.toISOString())

      const response = await api.get<ContentItemAnalyticsDto>(
        `/v1/content-hub/analytics/items/${itemId}?${params}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Record a content event
   */
  async recordEvent(request: RecordContentEventRequest): Promise<void> {
    try {
      await api.post('/v1/content-hub/analytics/events', request)
    } catch (error) {
      // Silently fail for analytics events
      console.error('Failed to record event:', error)
    }
  },

  /**
   * Record a view event
   */
  async recordViewEvent(itemId: string, appContext?: string): Promise<void> {
    try {
      const params = new URLSearchParams()
      if (appContext) params.set('appContext', appContext)

      await api.post(`/v1/content-hub/analytics/events/view/${itemId}?${params}`)
    } catch (error) {
      // Silently fail for analytics events
      console.error('Failed to record view event:', error)
    }
  },

  /**
   * Export analytics as CSV
   */
  async exportAnalytics(startDate?: Date, endDate?: Date): Promise<Blob> {
    try {
      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate.toISOString())
      if (endDate) params.set('endDate', endDate.toISOString())
      params.set('format', 'csv')

      const response = await api.get<Blob>(`/v1/content-hub/analytics/export?${params}`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
